import { useState, useEffect, useRef, useCallback } from 'react';
import Big from 'big.js';
import { useDispatch } from 'react-redux';
import {
    Combobox,
    CustomTableRef,
    ETypeOfNotification,
    InputDate,
    setNotification,
    GetItemsProps,
} from 'medere-ui-framework';
import { Button } from 'primereact/button';
import { confirmDialog } from 'primereact/confirmdialog';
import { getAllCompanies } from '../../servicesHooks/Accounting/useAccountingService';
import { getHealthEntityComboOptionsWithAll } from '../../services/digitalRPservice';
import { getProfessionals } from '../../servicesHooks/Budget/useBudgetService';
import { getFinancerPendingProfessionals } from '../../servicesHooks/Honorarium/useHonorariumLiquidationService';
import { createFinancerPreLiquidation } from '../../servicesHooks/Honorarium/useHonorariumPreLiquidationService';
import { getAllPaymentMethods } from '../../services/FinancialAccountController';
import { parseApiError } from '../../services/apiError';
import { PaymentMethodDTO } from '../../dto/FinancialAccountDTO';
import { FinancerProfessionalSummaryDTO, PaginatedFinancerProfessionalSummaryDTO } from '../../dto/FinancerDistributionDTO';
import { ComboDataDTO, defaultAllComboDataDTO } from '../../dto/ComboDataDTO';
import FinancerSummaryTable from './FinancerSummaryTable';
import FinancerDetailTable from './FinancerDetailTable';
import FilterMultiSelect from './FilterMultiSelect';
import {
    AppliedSearch, BENEFIT_ORDER_STATE_OPTIONS, FORM_STATE_OPTIONS, PAYER_TYPE_OPTIONS, SelectOption,
    buildPendingQueryParams, emptyToUndefined, formatDateParam, formatMoney,
} from './financerDistributionFormat';
import './FinancerDistributionPanel.scss';

/** Opción del combo de entidad facturadora (getAllCompanies). */
interface BillingEntityOption {
    company: number;
    description: string;
}

const ALL_PROFESSIONALS = -1;

function toFilterId(option: ComboDataDTO | null): number {
    return option?.id != null && option.id > 0 ? option.id : -1;
}

function sumTotals(rows: FinancerProfessionalSummaryDTO[]): Big {
    return rows.reduce((total, r) => total.plus(r.totalProfessionalAmount), new Big(0));
}

interface FinancerDistributionPanelProps {
    /** Se llama después de crear. Si no viene, el panel recarga el resumen. */
    onCreated?: () => void;
}

/**
 * Distribución FINANCER: filtros, resumen por profesional, detalle y creación de pre-liquidaciones.
 * No renderiza <ConfirmDialog />: confirmDialog() es global y lo pone la vista que lo contiene.
 */
export default function FinancerDistributionPanel({ onCreated }: FinancerDistributionPanelProps) {
    const dispatch = useDispatch();

    // Filtros (sin aplicar hasta "Buscar")
    const [selectedCompany, setSelectedCompany] = useState<BillingEntityOption | null>(null);
    const [selectedProfessional, setSelectedProfessional] = useState<ComboDataDTO>(defaultAllComboDataDTO);
    const [selectedHealthEntity, setSelectedHealthEntity] = useState<ComboDataDTO>(defaultAllComboDataDTO);
    const [fromDate, setFromDate] = useState<Date | null>(() => {
        const d = new Date(); d.setMonth(d.getMonth() - 1); return d;
    });
    const [toDate, setToDate] = useState<Date | null>(new Date());
    const [orderStates, setOrderStates] = useState<number[]>([]);
    const [formStates, setFormStates] = useState<number[]>([]);
    const [paymentMethods, setPaymentMethods] = useState<number[]>([]);
    const [payerTypes, setPayerTypes] = useState<number[]>([]);
    const [paymentMethodOptions, setPaymentMethodOptions] = useState<SelectOption[]>([]);

    useEffect(() => {
        getAllPaymentMethods()
            .then((methods: PaymentMethodDTO[]) =>
                setPaymentMethodOptions(methods.map(m => ({ label: m.description, value: m.paymentMethodId })))
            )
            .catch((error: unknown) => parseApiError(error));
    }, []);

    // Resumen: vive acá para conservar lo cargado y la selección al volver del detalle.
    // La tabla carga de a páginas con el scroll (CustomTable) y lee los filtros aplicados del ref.
    const [appliedSearch, setAppliedSearch] = useState<AppliedSearch | null>(null);
    const appliedSearchRef = useRef<AppliedSearch | null>(null);
    const summaryTableRef = useRef<CustomTableRef>(null);
    // Totales del servidor sobre todo el resultado: llegan con la primera página
    const [summary, setSummary] = useState<PaginatedFinancerProfessionalSummaryDTO | null>(null);
    const [selection, setSelection] = useState<FinancerProfessionalSummaryDTO[]>([]);
    const [detailProfessional, setDetailProfessional] = useState<FinancerProfessionalSummaryDTO | null>(null);
    const [loadingCreate, setLoadingCreate] = useState(false);

    const getSummaryRows = useCallback(async (offset: number, pageSize: number): Promise<FinancerProfessionalSummaryDTO[]> => {
        const search = appliedSearchRef.current;
        if (!search) return [];
        try {
            const data: PaginatedFinancerProfessionalSummaryDTO =
                await getFinancerPendingProfessionals(buildPendingQueryParams(search, search.professionalId, offset, pageSize));
            if (offset === 0) setSummary(data);
            return data.items ?? [];
        } catch (error: unknown) {
            // CustomTable no captura el error: sin esto queda el spinner colgado
            parseApiError(error);
            dispatch(setNotification({
                title: 'No se pudo cargar el resumen. Intentá de nuevo.',
                icon: 'pi-times', typeOfNotification: ETypeOfNotification.ERROR,
            }));
            return [];
        }
    }, [dispatch]);

    /** Vuelve a cargar el resumen desde la primera página con la búsqueda dada. */
    function reloadSummary(search: AppliedSearch) {
        appliedSearchRef.current = search;
        setSummary(null);
        // La primera búsqueda monta la tabla, que carga sola; las siguientes la refrescan
        if (appliedSearch) summaryTableRef.current?.refreshTable?.();
        setAppliedSearch(search);
    }

    // ── Handlers ──

    function handleSearch() {
        if (!selectedCompany) {
            dispatch(setNotification({
                title: 'Seleccioná una entidad facturadora.',
                icon: 'pi-times', typeOfNotification: ETypeOfNotification.ERROR,
            }));
            return;
        }
        if (!fromDate || !toDate) {
            dispatch(setNotification({
                title: 'Seleccioná el rango de fechas.',
                icon: 'pi-times', typeOfNotification: ETypeOfNotification.ERROR,
            }));
            return;
        }
        const search: AppliedSearch = {
            billingEntityId: selectedCompany.company,
            professionalId: toFilterId(selectedProfessional),
            healthEntityId: toFilterId(selectedHealthEntity),
            fromDate,
            toDate,
            formStates,
            orderStates,
            paymentMethods,
            payerTypes,
        };
        setDetailProfessional(null);
        setSelection([]);
        reloadSummary(search);
    }

    function confirmCreate(search: AppliedSearch, professionalCount: number, total: Big | number, professionalIds?: number[]) {
        confirmDialog({
            header: '¿Crear pre-liquidaciones?',
            message: `Se va a crear una pre-liquidación por profesional para ${professionalCount} profesional(es), por un total de ${formatMoney(total)}.`,
            icon: 'pi pi-info-circle',
            acceptLabel: 'Crear',
            rejectLabel: 'Cancelar',
            accept: () => doCreate(search, professionalIds),
        });
    }

    function handleCreateForSelected() {
        if (!appliedSearch || selection.length === 0) return;
        confirmCreate(appliedSearch, selection.length, sumTotals(selection), selection.map(p => p.professional));
    }

    /** Todos los del resultado, aunque estén en otras páginas: el backend toma todos los pendientes con los filtros aplicados. */
    function handleCreateForAll() {
        if (!appliedSearch || !summary) return;
        const professionalIds = appliedSearch.professionalId !== ALL_PROFESSIONALS ? [appliedSearch.professionalId] : undefined;
        confirmCreate(appliedSearch, summary.totalCount, summary.totalProfessionalAmount, professionalIds);
    }

    /** `professionalIds` vacío = todos los profesionales con pendientes para la búsqueda. */
    async function doCreate(search: AppliedSearch, professionalIds?: number[]) {
        setLoadingCreate(true);
        try {
            await createFinancerPreLiquidation({
                billingEntityId: search.billingEntityId,
                fromDate: formatDateParam(search.fromDate),
                toDate: formatDateParam(search.toDate),
                healthEntityId: search.healthEntityId !== -1 ? search.healthEntityId : null,
                professionalIds,
                formStateTypes: emptyToUndefined(search.formStates),
                benefitOrderStates: emptyToUndefined(search.orderStates),
                paymentMethodIds: emptyToUndefined(search.paymentMethods),
                payerTypes: emptyToUndefined(search.payerTypes),
            });
            dispatch(setNotification({
                title: 'Pre-liquidaciones creadas.',
                icon: 'pi-check-circle', typeOfNotification: ETypeOfNotification.INFO,
            }));
            setSelection([]);
            if (onCreated) {
                onCreated();
                return;
            }
            reloadSummary(search);
        } catch (error: unknown) {
            const apiError = parseApiError(error);
            dispatch(setNotification({
                title: apiError.status === 422
                    ? 'No hay ítems pendientes para los profesionales seleccionados.'
                    : 'No se pudieron crear las pre-liquidaciones. Intentá de nuevo.',
                icon: 'pi-times', typeOfNotification: ETypeOfNotification.ERROR,
            }));
        } finally {
            setLoadingCreate(false);
        }
    }

    return (
        <div className="financer-distribution">
            {/* Filtros */}
            <div className="flex gap-3 align-items-end flex-wrap" style={{ marginBottom: 'var(--space-4)' }}>
                <Combobox
                    label="Entidad facturadora"
                    getItems={(p: GetItemsProps) => getAllCompanies(p)}
                    optionLabel="description"
                    value={selectedCompany}
                    setValue={setSelectedCompany}
                    placeholder="Seleccionar"
                    style={{ width: '220px' }}
                    required
                />
                <Combobox
                    label="Profesional"
                    getItems={getProfessionals}
                    optionLabel="name"
                    value={selectedProfessional}
                    setValue={(e: ComboDataDTO) => setSelectedProfessional(e ?? defaultAllComboDataDTO)}
                    placeholder="Todos"
                    style={{ width: '220px' }}
                />
                <Combobox
                    label="Obra Social"
                    getItems={getHealthEntityComboOptionsWithAll}
                    optionLabel="name"
                    value={selectedHealthEntity}
                    setValue={(e: ComboDataDTO) => setSelectedHealthEntity(e ?? defaultAllComboDataDTO)}
                    placeholder="Todas"
                    style={{ width: '200px' }}
                />
                <InputDate
                    label="Desde"
                    value={fromDate}
                    onChange={(e: { value: Date | null }) => setFromDate(e.value)}
                    style={{ width: '150px' }}
                />
                <InputDate
                    label="Hasta"
                    value={toDate}
                    onChange={(e: { value: Date | null }) => setToDate(e.value)}
                    style={{ width: '150px' }}
                />
                <FilterMultiSelect label="Pagador" value={payerTypes} options={PAYER_TYPE_OPTIONS} onChange={setPayerTypes} />
                <FilterMultiSelect label="Estado orden" value={orderStates} options={BENEFIT_ORDER_STATE_OPTIONS} onChange={setOrderStates} />
                <FilterMultiSelect label="Estado factura de la orden" value={formStates} options={FORM_STATE_OPTIONS} onChange={setFormStates} />
                <FilterMultiSelect label="Medio de pago de la orden" value={paymentMethods} options={paymentMethodOptions} onChange={setPaymentMethods} />
                <Button
                    outlined
                    className="md-ghost"
                    icon="pi pi-search"
                    label="Buscar"
                    onClick={handleSearch}
                    // Mismo margen inferior que .inputContainer, para que align-items-end lo alinee con los campos
                    style={{ marginBottom: 'var(--space-2)' }}
                />
            </div>

            {appliedSearch && detailProfessional && (
                <>
                    <div>
                        <Button
                            text
                            icon="pi pi-angle-double-left"
                            label="Volver"
                            onClick={() => setDetailProfessional(null)}
                            style={{ color: 'var(--primary-600)', fontWeight: 600, paddingLeft: 0 }}
                        />
                    </div>
                    <p className="financer-subtitle">{detailProfessional.professionalName}</p>
                    <FinancerDetailTable
                        search={appliedSearch}
                        professionalId={detailProfessional.professional}
                        paymentMethodOptions={paymentMethodOptions}
                    />
                </>
            )}

            {/* El resumen queda montado pero oculto en el detalle, así conserva su estado */}
            {appliedSearch && (
                <div style={{ display: detailProfessional ? 'none' : undefined }}>
                    {summary && (
                        <div className="flex justify-content-end" style={{ marginBottom: 'var(--space-3)' }}>
                            <span style={{ color: 'var(--text-2)', fontSize: '0.9rem' }}>
                                {summary.totalCount} profesional(es)
                                {' — Créditos: '}<strong>{formatMoney(summary.totalCreditAmount)}</strong>
                                {' — Débitos: '}<strong>{formatMoney(summary.totalDebitAmount)}</strong>
                                {' — Total: '}<strong>{formatMoney(summary.totalProfessionalAmount)}</strong>
                            </span>
                        </div>
                    )}

                    <FinancerSummaryTable
                        tableRef={summaryTableRef}
                        getItems={getSummaryRows}
                        selection={selection}
                        onSelectionChange={setSelection}
                        onViewDetail={setDetailProfessional}
                    />

                    {summary && summary.totalCount > 0 && (
                        <div className="flex align-items-center justify-content-between financer-action-bar">
                            <span style={{ color: 'var(--text-2)' }}>
                                {selection.length > 0
                                    ? <>{selection.length} profesional(es) seleccionado(s){' — '}<strong>{formatMoney(sumTotals(selection))}</strong></>
                                    : <>{summary.totalCount} profesional(es) en el resultado{' — '}<strong>{formatMoney(summary.totalProfessionalAmount)}</strong></>}
                            </span>
                            <div className="flex gap-2">
                                <Button
                                    outlined
                                    className="md-ghost"
                                    label="Crear para todos"
                                    onClick={handleCreateForAll}
                                    disabled={loadingCreate}
                                />
                                <Button
                                    icon="pi pi-plus-circle"
                                    className="md-primary"
                                    label="Crear para seleccionados"
                                    onClick={handleCreateForSelected}
                                    disabled={selection.length === 0}
                                    loading={loadingCreate}
                                />
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
